import { type JSX, useCallback, useEffect, useId, useRef, useState } from 'react';

import {
  type Dublettenverdacht,
  type Kanaleingabe,
  type Kundenart,
  anlegen as apiAnlegen,
  dubletten as apiDubletten,
} from '../api/kunden';
import { ApiFehler, type Verstoss } from '../api/fehler';
import { Abschnitt, Feldgruppe, Feldraster } from '../komponenten/Akte';
import { Erfassung } from '../komponenten/Erfassung';
import { Feld } from '../komponenten/Feld';
import { Meldung } from '../komponenten/Meldung';

type Formular = {
  kundenart: Kundenart;
  firmierung: string;
  rechtsform: string;
  ustidnr: string;
  leitwegId: string;
  vorname: string;
  nachname: string;
  strasse: string;
  plz: string;
  ort: string;
  email: string;
  telefon: string;
  apVorname: string;
  apNachname: string;
  apFunktion: string;
  apEmail: string;
  apTelefon: string;
};

/**
 * Die Felder, die ihre Verstoesse selbst zeigen (ERR-03). Die Meldung ueber
 * dem Formular fuehrt nur, was an KEINEM sichtbaren Feld haengt — sonst
 * stuende derselbe Satz zweimal.
 */
const AM_FELD = ['firmierung', 'nachname', 'ustidnr'] as const;

/** SUP-10: was eine andere Maske vorbelegt — etwa der Posteingang aus Absender und Signatur. */
export type Kundenvorgabe = Partial<Pick<Formular,
  'kundenart' | 'firmierung' | 'vorname' | 'nachname' | 'email' | 'telefon'
  | 'apVorname' | 'apNachname' | 'apEmail' | 'apTelefon'>>;

const LEER: Formular = {
  kundenart: 'geschaeft',
  firmierung: '',
  rechtsform: '',
  ustidnr: '',
  leitwegId: '',
  vorname: '',
  nachname: '',
  strasse: '',
  plz: '',
  ort: '',
  email: '',
  telefon: '',
  apVorname: '',
  apNachname: '',
  apFunktion: '',
  apEmail: '',
  apTelefon: '',
};

/**
 * Aus zwei Eingabefeldern werden null bis zwei Kanaele (KUN-04).
 *
 * Leere Felder erzeugen keinen Kanal: Ein Kanal mit leerem Wert waere an der
 * CHECK-Bedingung der Tabelle gescheitert, und ein Formular soll nicht
 * senden, was es nicht hat.
 */
function kanaeleAus(email: string, telefon: string): readonly Kanaleingabe[] {
  return [
    ...(email.trim() === ''
      ? []
      : [{ art: 'email' as const, wert: email.trim(), bezeichnung: null }]),
    ...(telefon.trim() === ''
      ? []
      : [{ art: 'telefon' as const, wert: telefon.trim(), bezeichnung: null }]),
  ];
}

/**
 * M-11 Kunde anlegen — Neuanlage im Telefongespraech.
 *
 * Die Vorgabe nach NFA-21 lautet: hoechstens SECHS Bedienschritte, unter 90
 * Sekunden, EINSCHLIESSLICH eines Ansprechpartners. Das ist der Grund fuer
 * zwei Entscheidungen, die man dem Formular sonst als Nachlaessigkeit
 * auslegen koennte:
 *
 *   * Ein Formular ohne Reiter. Adresse, Kommunikation und der erste
 *     Ansprechpartner stehen untereinander im selben Bild. Ein zweiter Schritt
 *     fuer den Ansprechpartner reisst die Vorgabe.
 *   * Der Dublettenverdacht steht als Streifen UEBER den Feldern und
 *     blockiert nichts. Ein blockierender Dublettenschutz erzeugt Kunden mit
 *     Tippfehler im Namen (KUN-23).
 *
 * Gegliedert ist es in drei benannte Abschnitte — Firma beziehungsweise
 * Person, Anschrift, Ansprechpartner —, weil ein Formular aus siebzehn gleich
 * aussehenden Feldern untereinander keine Ordnung hat, in der man sich
 * zurechtfindet. Die Kundenart steht als eigener Abschnitt davor: Sie ist die
 * einzige Angabe, die das Formular umbaut. Seit ÄA-41 ist das die Erfassung
 * des Entwurfs v2 — eine Karte, Speichern oben rechts und am Ende.
 *
 * Die Kundennummer wird erst beim Speichern vergeben und ist vorher als
 * solche gekennzeichnet — sie steht im Feld als "wird vergeben" und nicht als
 * leeres Kaestchen, das jemand ausfuellen moechte.
 */
export function KundeAnlegen({
  anfangsName = '',
  vorgabe,
  einzeln = false,
  beiAngelegt,
  beiAbbruch,
}: {
  readonly anfangsName?: string;
  /** SUP-10: Vorbelegung; der Anwender ueberschreibt sie. */
  readonly vorgabe?: Kundenvorgabe;
  /** Ohne "Speichern & neu" — wer aus einer Nachricht anlegt, legt genau einen Kunden an. */
  readonly einzeln?: boolean;
  readonly beiAngelegt: (id: string, kundennummer: string) => void;
  readonly beiAbbruch: () => void;
}): JSX.Element {
  const [formular, setzeFormular] = useState<Formular>({
    ...LEER,
    firmierung: anfangsName,
    ...vorgabe,
  });
  const [verdacht, setzeVerdacht] = useState<readonly Dublettenverdacht[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const erstesFeld = useRef<HTMLInputElement>(null);
  const wahlId = useId();

  const setze = <K extends keyof Formular>(feld: K, wert: Formular[K]): void =>
    setzeFormular((f) => ({ ...f, [feld]: wert }));

  const name =
    formular.kundenart === 'privat'
      ? `${formular.vorname} ${formular.nachname}`.trim()
      : formular.firmierung;

  /**
   * KUN-23: Der Verdacht laeuft WAEHREND der Eingabe.
   *
   * Er blockiert nicht und wird nicht beim Speichern nachgeholt: Ein Hinweis,
   * der erst nach dem Absenden kommt, kostet den Anwender die Erfassung.
   */
  useEffect(() => {
    if (name.trim().length < 3 && formular.ustidnr === '') {
      setzeVerdacht([]);
      return;
    }
    const kennung = setTimeout(() => {
      void apiDubletten(name, formular.ustidnr === '' ? null : formular.ustidnr)
        .then(setzeVerdacht)
        // Ein gescheiterter Verdacht darf die Erfassung nicht stoeren — er ist
        // eine Hilfe, keine Voraussetzung.
        .catch(() => setzeVerdacht([]));
    }, 400);
    return () => clearTimeout(kennung);
  }, [name, formular.ustidnr]);

  const speichern = useCallback(
    async (undNeu: boolean): Promise<void> => {
      setzeLaeuft(true);
      setzeFehler(undefined);
      try {
        const ergebnis = await apiAnlegen({
          kundenart: formular.kundenart,
          firmierung: formular.kundenart === 'privat' ? null : formular.firmierung,
          person:
            formular.kundenart === 'privat'
              ? {
                  anrede: null,
                  titel: null,
                  vorname: formular.vorname === '' ? null : formular.vorname,
                  nachname: formular.nachname,
                  geburtsdatum: null,
                }
              : null,
          rechtsform: formular.rechtsform === '' ? null : formular.rechtsform,
          ustidnr: formular.ustidnr === '' ? null : formular.ustidnr,
          leitwegId: formular.leitwegId === '' ? null : formular.leitwegId,
          adresse:
            formular.strasse === ''
              ? null
              : {
                  typ: 'rechnung',
                  zusatz: null,
                  strasse: formular.strasse,
                  plz: formular.plz,
                  ort: formular.ort,
                  land: 'DE',
                },
          // KUN-04: Was der Anwender getippt hat, wird zum Kanal. Der erste
          // seiner Art ist der primaere — das entscheidet der Server, damit
          // es EINE Regel bleibt.
          kanaele: kanaeleAus(formular.email, formular.telefon),
          ansprechpartner:
            formular.apNachname === ''
              ? null
              : {
                  person: {
                    anrede: null,
                    titel: null,
                    vorname: formular.apVorname === '' ? null : formular.apVorname,
                    nachname: formular.apNachname,
                    geburtsdatum: null,
                  },
                  funktion: formular.apFunktion === '' ? null : formular.apFunktion,
                  kanaele: kanaeleAus(formular.apEmail, formular.apTelefon),
                },
        });

        if (undNeu) {
          // "Speichern und neu" haelt das Formular offen fuer den naechsten
          // Fall — der Anwender am Telefon hat oft mehrere.
          setzeFormular({ ...LEER, kundenart: formular.kundenart });
          setzeVerdacht([]);
          setzeRueckmeldung(`${ergebnis.kundennummer} angelegt. Das Formular ist wieder leer.`);
          erstesFeld.current?.focus();
        } else {
          beiAngelegt(ergebnis.id, ergebnis.kundennummer);
        }
      } catch (ursache) {
        setzeFehler(ursache as ApiFehler);
      } finally {
        setzeLaeuft(false);
      }
    },
    [formular, beiAngelegt],
  );

  /** Tastaturweg nach M-11: Strg+S speichert, Strg+Umschalt+S speichert und neu. */
  useEffect(() => {
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if ((ereignis.ctrlKey || ereignis.metaKey) && ereignis.key.toLowerCase() === 's') {
        ereignis.preventDefault();
        void speichern(ereignis.shiftKey && !einzeln);
      }
    };
    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, [speichern, einzeln]);

  const verstoesseZu = (feld: string): readonly Verstoss[] => fehler?.alleZumFeld(feld) ?? [];

  return (
    /*
     * ÄA-41, Seitentyp Erfassung: EINE Karte mit aufklappbaren Abschnitten.
     * Die Erfassung ist das Formular — noValidate wie in allen Formularen
     * dieser Oberflaeche, weil die Sprechblase des Browsers weder die drei
     * Teile aus ERR-01 traegt noch nach ERR-04 ALLE Verstoesse nennt.
     * Geprueft wird fachlich, und die Antwort kommt am Feld an (ERR-03).
     *
     * Alle Abschnitte stehen offen: Ein zugeklappter Abschnitt kostete einen
     * Klick und risse die sechs Bedienschritte nach NFA-21.
     */
    <div className="maske maske--weit erfassung">
      <Erfassung
        beschriftung="Kunde anlegen"
        beiAbsenden={() => void speichern(false)}
        absenden={{ text: laeuft ? 'Wird gespeichert …' : 'Speichern', gesperrt: laeuft }}
        abbrechen={{ text: 'Abbrechen', beiKlick: beiAbbruch }}
        zusatz={einzeln ? undefined : (
          <button
            type="button"
            className="knopf"
            disabled={laeuft}
            onClick={() => void speichern(true)}
          >
            Speichern &amp; neu
          </button>
        )}
      >
        {/*
          Die Kundenart steht als erster Abschnitt, weil sie als einzige
          Angabe das Formular UMBAUT: Privatkunden haben keine USt-IdNr.,
          oeffentliche brauchen eine Leitweg-ID, Geschaeftskunden einen
          Ansprechpartner. Wer sie spaeter aendert, findet ein anderes
          Formular vor — das darf keine Ueberraschung am Rand sein.
        */}
        <Abschnitt titel="Kundenart">
          <fieldset className="wahlband">
            {/* Der Abschnittskopf zeigt den Namen schon; die Legende nennt ihn
                der Vorlesehilfe als Name der Gruppe. */}
            <legend className="nur-vorlesen">Kundenart</legend>
            <div className="wahlband__reihe">
              {(
                [
                  /*
                    DIE ERKLAERUNG SAGT AUCH, WAS DIE WAHL AUSLOEST (RNG-15).
                    Seit dem 15.09.2026 entscheidet die Kundenart ueber das
                    Ausgabeformat der Rechnung — bei „Oeffentlich" stand es
                    schon da, bei den beiden anderen nicht. Eine Vorbelegung,
                    die niemand nennt, ist eine stille Annahme.
                  */
                  ['privat', 'Privat', 'Einzelperson · Rechnung als PDF'],
                  ['geschaeft', 'Geschäft', 'Firma oder Verein · Rechnung als ZUGFeRD'],
                  ['oeffentlich', 'Öffentlich', 'Behörde · Rechnung als XRechnung'],
                ] as const
              ).map(([art, name, erklaerung]) => {
                const id = `${wahlId}-${art}`;
                return (
                  /*
                    Die Erklaerung steht NEBEN der Beschriftung und nicht in
                    ihr: Im <label> waere sie Teil des zugaenglichen Namens,
                    und ein Vorleseprogramm meldete "Privat Einzelperson
                    Auswahlknopf". Als aria-describedby kommt sie danach — als
                    das, was sie ist.
                  */
                  <div key={art} className="wahlband__wahl">
                    <input
                      id={id}
                      type="radio"
                      name="kundenart"
                      value={art}
                      checked={formular.kundenart === art}
                      onChange={() => setze('kundenart', art)}
                      aria-describedby={`${id}-erklaerung`}
                    />
                    <label className="wahlband__name" htmlFor={id}>
                      {name}
                    </label>
                    <span className="wahlband__erklaerung" id={`${id}-erklaerung`}>
                      {erklaerung}
                    </span>
                  </div>
                );
              })}
            </div>
          </fieldset>
        </Abschnitt>

        {/* ERR-04: alles, was nicht am Feld haengt, gemeinsam ueber den Feldern. */}
        {fehler !== undefined && <Meldung fehler={fehler} gezeigteFelder={AM_FELD} />}

        <p role="status" className="nur-vorlesen">
          {rueckmeldung}
        </p>

        {/* KUN-23: der Verdacht als Streifen ueber den Feldern, nicht blockierend. */}
        {verdacht.length > 0 && (
          <div className="dubletten" role="status">
            <strong>Möglicherweise schon vorhanden.</strong> Die Anlage bleibt möglich — prüfen
            Sie kurz:
            <ul className="dubletten__treffer">
              {verdacht.map((v) => (
                <li key={v.id}>
                  <span className="dubletten__nummer">{v.kundennummer}</span>
                  <span>
                    {v.name}
                    {v.ort !== null && <span className="dubletten__wo"> · {v.ort}</span>}
                    {v.grund === 'ustidnr' && (
                      <span className="dubletten__wo"> · gleiche USt-IdNr.</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Links im Kopf der Name, rechts die leise Ergaenzung — sie steht im
            Zusatz des Abschnitts und nicht im Titel. */}
        <Abschnitt
          titel={formular.kundenart === 'privat' ? 'Person' : 'Firma'}
          zusatz="* = Pflichtangabe"
        >
          <Feldraster>
            {formular.kundenart === 'privat' ? (
              <>
                <Feld
                  beschriftung="Vorname"
                  wert={formular.vorname}
                  aendern={(w) => setze('vorname', w)}
                  verweis={erstesFeld}
                />
                <Feld
                  beschriftung="Nachname"
                  wert={formular.nachname}
                  aendern={(w) => setze('nachname', w)}
                  pflicht
                  verstoesse={verstoesseZu('nachname')}
                />
              </>
            ) : (
              <>
                <Feld
                  beschriftung="Firmierung"
                  wert={formular.firmierung}
                  aendern={(w) => setze('firmierung', w)}
                  pflicht
                  verstoesse={verstoesseZu('firmierung')}
                  verweis={erstesFeld}
                />
                <Feld
                  beschriftung="Rechtsform"
                  wert={formular.rechtsform}
                  aendern={(w) => setze('rechtsform', w)}
                  hinweisText="GmbH, e. K., e. V. …"
                />
              </>
            )}

            {/* M-11: vorher als solche gekennzeichnet, kein leeres Kaestchen. */}
            <div className="feld feld--gesperrt feld--reserviert">
              <span className="feld__beschriftung">Kundennummer</span>
              <p className="feld__ersatz">wird beim Speichern vergeben</p>
            </div>

            {/* KUN-07 */}
            {formular.kundenart !== 'privat' && (
              <Feld
                beschriftung="USt-IdNr."
                wert={formular.ustidnr}
                aendern={(w) => setze('ustidnr', w)}
                hinweisText="DE123456789"
                hilfe="Wird nach dem Speichern im qualifizierten Bestätigungsverfahren geprüft. Bis dahin steht die Prüfung als offen am Kunden."
                verstoesse={verstoesseZu('ustidnr')}
              />
            )}

            {/* RNG-16 */}
            {formular.kundenart === 'oeffentlich' && (
              <Feld
                beschriftung="Leitweg-ID"
                wert={formular.leitwegId}
                aendern={(w) => setze('leitwegId', w)}
                hinweisText="991-12345-67"
                hilfe="Ohne sie lässt sich später keine E-Rechnung festschreiben. Die Anlage bleibt trotzdem möglich."
              />
            )}
          </Feldraster>
        </Abschnitt>

        <Abschnitt titel="Anschrift und Erreichbarkeit" zusatz="Rechnungsanschrift">
          {/*
            Strasse, PLZ und Ort stehen im Raster hintereinander: Sie werden
            in einem Zug getippt und sind nach Kapitel 07 EIN Bedienschritt
            (NFA-21).

            Danach die Erreichbarkeit des KUNDEN — bei einer Firma die
            Zentrale und das Sammelpostfach, beim Privatkunden seine eigene.
            Beides wird nach KUN-04 zum Kanal am Kunden. Bis Migration 0023
            gab es dieses Feld fuer Firmen ueberhaupt nicht, und der
            eingetippte Wert wurde beim Speichern still verworfen —
            fach.kunde hatte keine Spalte dafuer.

            Zwei Untergruppen (CRM-218): Im gemeinsamen Raster rutschte die
            E-Mail in die Reihe der Anschrift und das Telefon allein in die
            naechste. Die Tab-Folge bleibt dieselbe.
          */}
          <Feldgruppe titel="Anschrift">
            <Feldraster>
              <Feld
                beschriftung="Straße und Hausnummer"
                wert={formular.strasse}
                aendern={(w) => setze('strasse', w)}
              />
              <Feld
                beschriftung="PLZ"
                wert={formular.plz}
                aendern={(w) => setze('plz', w)}
                eingabeart="numeric"
              />
              <Feld beschriftung="Ort" wert={formular.ort} aendern={(w) => setze('ort', w)} />
            </Feldraster>
          </Feldgruppe>
          <Feldgruppe titel="Erreichbarkeit">
            <Feldraster>
              <Feld
                beschriftung="E-Mail"
                art="email"
                wert={formular.email}
                aendern={(w) => setze('email', w)}
              />
              <Feld
                beschriftung="Telefon"
                wert={formular.telefon}
                aendern={(w) => setze('telefon', w)}
                eingabeart="numeric"
              />
            </Feldraster>
          </Feldgruppe>
        </Abschnitt>

        {formular.kundenart !== 'privat' && (
          /* Der Schritt, der die Vorgabe sonst reisst: Bei Geschaeftskunden
             kommt der Ansprechpartner fast immer mit (M-11, KUN-08). */
          <Abschnitt
            titel="Ansprechpartner"
            zusatz={formular.apNachname === '' ? 'wird mit angelegt' : 'wird angelegt'}
          >
            <Feldraster>
              <Feld
                beschriftung="Vorname"
                wert={formular.apVorname}
                aendern={(w) => setze('apVorname', w)}
              />
              <Feld
                beschriftung="Nachname"
                wert={formular.apNachname}
                aendern={(w) => setze('apNachname', w)}
              />
              <Feld
                beschriftung="Funktion"
                wert={formular.apFunktion}
                aendern={(w) => setze('apFunktion', w)}
                hinweisText="Einkauf, Fuhrpark …"
              />
              <Feld
                beschriftung="E-Mail"
                art="email"
                wert={formular.apEmail}
                aendern={(w) => setze('apEmail', w)}
              />
              <Feld
                beschriftung="Durchwahl"
                wert={formular.apTelefon}
                aendern={(w) => setze('apTelefon', w)}
                eingabeart="numeric"
              />
            </Feldraster>
            <p className="feld__hilfe">
              Ohne Nachnamen wird kein Ansprechpartner angelegt — die übrigen Angaben gehen dann
              nicht mit.
            </p>
          </Abschnitt>
        )}
      </Erfassung>
    </div>
  );
}
